import { clsx } from 'clsx';
import moment from 'moment-timezone';
import { twMerge } from 'tailwind-merge';
 
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import Request from 'files/Request.js';

const Utils = {
    adjustColor: (color, percent) => {
        var R = parseInt(color.substring(1,3),16);
        var G = parseInt(color.substring(3,5),16);
        var B = parseInt(color.substring(5,7),16);

        R = parseInt(R * (100 + percent) / 100);
        G = parseInt(G * (100 + percent) / 100);
        B = parseInt(B * (100 + percent) / 100);

        R = (R<255)?R:255;
        G = (G<255)?G:255;
        B = (B<255)?B:255;

        var RR = ((R.toString(16).length==1)?"0"+R.toString(16):R.toString(16));
        var GG = ((G.toString(16).length==1)?"0"+G.toString(16):G.toString(16));
        var BB = ((B.toString(16).length==1)?"0"+B.toString(16):B.toString(16));

        return "#"+RR+GG+BB;
    },
    attributeForKey: {
        select: (e, key) => {
            let optionElement = e.target.childNodes[e.target.selectedIndex];
            return optionElement.getAttribute(key);
        }
    },
    cn: (...inputs) => {
        return twMerge(clsx(inputs));
    },
    copyText: val => {

        // create temporary text area and add to body 
        let textArea = document.createElement('textarea');
        textArea.value = val;
        textArea.style.opacity = 0;
        textArea.style.position = 'fixed';
        document.body.appendChild(textArea);

        // focus and select textarea
        textArea.focus();
        textArea.select();

        // copy contents of textarea to clipboard
        navigator.clipboard.writeText(textArea.value);
        document.body.removeChild(textArea);
    },
    format: {
        address: (props = {}) => {

            // return formatted address in accordance with google auto complete formatting
            let { administrative_area_level_1, country, locality, postal_code, street_address_1, street_address_2 } = props || {};
            if(administrative_area_level_1 && country && locality && postal_code && street_address_1) {
                return `${street_address_1}${street_address_2 ? `, ${street_address_2 }` : ''}. ${locality}, ${administrative_area_level_1} ${postal_code}, ${country}`;
            }
            // return ala-carte address components as they are found
            return [ street_address_1, street_address_2, locality, administrative_area_level_1, postal_code, country ].filter(val => {
                return val && val.toString().length > 1 ? true : false;
            }).join(', ');
        },
        bytes: bytes => {
            if (bytes === 0) {
                return '0 Bytes'
            };

            const k = 1000; // decimal-based
            const sizeUnits = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

            const i = Math.floor(Math.log(bytes) / Math.log(k));
            const value = parseFloat((bytes / Math.pow(k, i)).toFixed(2));

            return `${value} ${sizeUnits[i]}`;
        },
        date: (date, options = {}) => {

            const { exclude_time = false, minimal = false, utc = false } = options;

            // create moment date object
            const momentDate = typeof date?.utc === 'function' ? date : (utc ? moment.utc(date).local() : moment(date));
            
            if(!momentDate.isValid()) {
                return 'Date is not valid';
            }

            const now = moment();
            const yesterday = moment().subtract(1, 'day');
            
            // helper function to get format string based on exclude_time and minimal flags
            const getTimeFormat = (baseFormat) => {
                if (exclude_time) return baseFormat;
                return minimal ? 'h:mma' : `${baseFormat} [at] h:mma`;
            };

            // check for today
            if(now.isSame(momentDate, 'day')) {
                if(minimal) {
                    return momentDate.format('h:mma');
                }
                return momentDate.format(exclude_time ? '[Today] MMMM Do' : '[Today at] h:mma');
            }

            // check for yesterday
            if(yesterday.isSame(momentDate, 'day')) {
                if(minimal) {
                    return momentDate.format(exclude_time ? 'MM/DD' : 'MM/DD [at] h:mma');
                }
                return momentDate.format(exclude_time ? '[Yesterday] MMMM Do' : '[Yesterday at] h:mma');
            }

            // check for upcoming week (future dates within 6 days)
            if(momentDate.isAfter(now) && momentDate.isBefore(moment().add(7, 'days'))) {
                if(minimal) {
                    return momentDate.format(exclude_time ? 'MM/DD' : 'MM/DD [at] h:mma');
                }
                return momentDate.format(getTimeFormat('dddd MMMM Do'));
            }

            // check for current year
            if(now.isSame(momentDate, 'year')) {
                if(minimal) {
                    return momentDate.format(exclude_time ? 'MM/DD' : 'MM/DD [at] h:mma');
                }
                const baseFormat = exclude_time ? 'MMMM Do' : 'MMM Do';
                return momentDate.format(getTimeFormat(baseFormat));
            }

            // fallback for different years
            return momentDate.format(minimal ? 'MM/DD/YY' : 'MM/DD/YYYY');
        },
        duration: (date, minimal = false) => {
            if(!date) {
                return 'Unknown';
            }
            let unix_a = moment.utc(date).unix();
            let unix_b = moment.utc().unix();
            if(unix_b - unix_a < 86400) {
                return `${Utils.parseDuration(unix_b - unix_a, minimal)} ago`;
            }
            return Utils.format.date(date);
        },
        number: val => {
            let formatter = new Intl.NumberFormat(navigator.language || 'en-US');
            return formatter.format(val);
        },
        phoneNumber: (phoneNumber, countryCode = 'US') => {
            try {
                let instance = PhoneNumberUtil.getInstance();
                let result = instance.parse(phoneNumber, countryCode);
                return instance.format(result, PhoneNumberFormat.INTERNATIONAL);
            } catch(e) {
                return phoneNumber;
            }
        },
    },
    getRegionFromAnnotations: coordinates => {

        let northWest = {
            latitude: -90,
            longitude: 180
        };
        let southEast = {
            latitude: 90,
            longitude: -180
        };

        coordinates.forEach((coordinate) => {
            northWest.longitude = Math.min(northWest.longitude, isNaN(coordinate[1]) ? coordinate.location.longitude : coordinate[1]);
            northWest.latitude = Math.max(northWest.latitude, isNaN(coordinate[0]) ? coordinate.location.latitude : coordinate[0]);

            southEast.longitude = Math.max(southEast.longitude, isNaN(coordinate[1]) ? coordinate.location.longitude : coordinate[1]);
            southEast.latitude = Math.min(southEast.latitude, isNaN(coordinate[0]) ? coordinate.location.latitude : coordinate[0]);
        })
        return [
            [ northWest.longitude, northWest.latitude ],
            [ southEast.longitude, southEast.latitude ]
        ];
    },
    hexToRGBA: (hex, alpha) => {

        var c;
    	if(/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    		c= hex.substring(1).split('');
    		if(c.length === 3){
    			c= [c[0], c[0], c[1], c[1], c[2], c[2]];
    		}
    		c= '0x'+c.join('');
    		return 'rgba('+[(c>>16)&255, (c>>8)&255, c&255].join(',')+',' + alpha + ')';
    	}
    	return hex;
    },
    isMobile: () => {
        return window.innerWidth < 1200;
    },
    numberFormat: value => {
        if(value > 1000000) {
            return `${(parseFloat(value) / 1000000).toFixed(2)} M`;
        }
        if(value > 1000) {
            return `${(parseFloat(value) / 1000).toFixed(2)} K`;
        }
        return value % 1 !== 0 ? parseFloat(value).toFixed(1) : value;
    },
    oxfordImplode: (items = []) => {
        if(items.length === 0) {
            return null;
        }
        if(items.length === 1) {
            return items[0];
        }
        if(items.length === 2) {
            return `${items[0]} and ${items[1]}`;
        }
        let string = '';
        for(var i in items) {
            if(i > 0) {
                string += parseInt(i) === items.length - 1 ? ', and ' : ', ';
            }
            string += items[i];
        }
        return string;
    },
    parseDuration: (duration, options = {}) => {

        let { largestUnitOnly = false, minimal = false } = options;
        let totalSeconds = parseInt(duration || 0);

        // no additional logic is required if duration is 0
        if(totalSeconds === 0) {
            return '0 seconds';
        }

        // calculate time units
        let days = Math.floor(totalSeconds / 86400);
        let hours = Math.floor((totalSeconds % 86400) / 3600);
        let minutes = Math.floor((totalSeconds % 3600) / 60);
        let seconds = totalSeconds % 60;

        // helper function to format unit labels
        let formatUnit = (value, unit) => {

            if(value === 0) {
                return null;
            }

            // prepare labels for each unit using options
            let labels = {
                day: minimal ? 'day' : (value === 1 ? 'day' : 'days'),
                hour: minimal ? (value === 1 ? 'hr' : 'hrs') : (value === 1 ? 'hour' : 'hours'),
                minute: value === 1 ? 'minute' : 'minutes',
                second: value === 1 ? 'second' : 'seconds'
            }
            return `${value} ${labels[unit]}`;
        };

        // build array of non-zero time components
        let parts = [
            days > 0 ? formatUnit(days, 'day') : null,
            hours > 0 ? formatUnit(hours, 'hour') : null,
            minutes > 0 ? formatUnit(minutes, 'minute') : null,
            seconds > 0 ? formatUnit(seconds, 'second') : null
        ].filter(Boolean);

        // return largest unit only if requested
        if(largestUnitOnly && parts.length > 0) {
            return parts[0];
        }

        // handle different combinations
        if(parts.length === 0) {
            return '0 seconds';
        }
        if(parts.length === 1) {
            return parts[0];
        }
        if(parts.length === 2) {
            return `${parts[0]} and ${parts[1]}`;
        }

        // for 3+ parts: "X days, Y hours, and Z minutes"
        return parts.slice(0, -1).join(', ') + ', and ' + parts[parts.length - 1];
    },
    randomString: (length = 20) => {
        let result = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        return result.substring(0, length);
    },
    sleep: async seconds => {
        return new Promise(resolve => {
            setTimeout(resolve, (seconds * 1000));
        })
    },
    softNumberFormat: (val, digits = 0) => {
        return isNaN(val) ? 0 : parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: digits })
    },
    toCurrency: (value, currency) => {
        return parseFloat(value || 0).toLocaleString('en-US', {
            style: 'currency',
            currency: currency || 'USD'
        })
    },
    ucFirst: text => {
        return text ? (text.charAt(0).toUpperCase() + text.substring(1)) : '';
    },
    ucWords: text => {
        return text.replace('_', ' ').split(' ').map(Utils.ucFirst).join(' ');
    }
}
export default Utils;
